// frontend/src/hooks/useRequestActions.js
// One place that knows what the workshop's next move on a request is, so the
// dashboard queue and the full requests page can never disagree about it.
//
// Some moves are navigations (quote it, log the work, invoice it) and some are
// status transitions (schedule, start, complete). `run` dispatches on status;
// callers render <ScheduleRequestModal {...scheduleModal} /> for the one move
// that needs to ask for a date first.

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../context/AuthContext';
import { scheduleRequest, startRequest, completeRequest } from '../api/requests';

// The dashboard and the requests page cache the same rows under different keys.
// A transition fired from either surface has to refresh both or the other one
// keeps showing the old status until its next poll.
const REQUEST_QUERY_KEYS = [['workshop-requests'], ['maintenance-requests']];

const vehicleIdOf = (r) => r?.vehicleId?._id || r?.vehicleId || r?.vehicle?._id;

const errText = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

export default function useRequestActions() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const [scheduling, setScheduling] = useState(null); // request awaiting a date
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState(null);

  const refresh = useCallback(() => {
    REQUEST_QUERY_KEYS.forEach((key) => queryClient.invalidateQueries({ queryKey: key }));
  }, [queryClient]);

  const schedule = useMutation({
    mutationFn: ({ id, date, note }) => scheduleRequest(id, date, note, token),
    onSuccess: () => { refresh(); setScheduling(null); setActionError(null); },
    onError: (err) => setActionError(errText(err, 'Échec de la planification.')),
  });

  const start = useMutation({
    mutationFn: ({ id }) => startRequest(id, token),
    onMutate: ({ id }) => { setBusyId(id); setActionError(null); },
    onSuccess: refresh,
    onError: (err) => setActionError(errText(err, 'Impossible de démarrer les travaux.')),
    onSettled: () => setBusyId(null),
  });

  const complete = useMutation({
    mutationFn: ({ id }) => completeRequest(id, token),
    onMutate: ({ id }) => { setBusyId(id); setActionError(null); },
    onSuccess: refresh,
    onError: (err) => setActionError(errText(err, 'Impossible de clôturer les travaux.')),
    onSettled: () => setBusyId(null),
  });

  // The next move for a request, by status. Returns false when there is
  // nothing to do (cancelled / refused), so callers can hide the button.
  const run = useCallback((r) => {
    if (!r) return false;
    switch (r.status) {
      case 'pending':
        // No /workshop/quotations/new route exists — the quotations page reads
        // ?request= and opens the form itself.
        navigate(`/workshop/quotations?request=${r._id}`);
        return true;
      case 'accepted':
      case 'rescheduled':
        setActionError(null);
        setScheduling(r);
        return true;
      case 'confirmed':
        start.mutate({ id: r._id });
        return true;
      case 'in-progress': {
        const vid = vehicleIdOf(r);
        navigate(vid ? `/workshop/log-maintenance?vehicle=${vid}` : '/workshop/log-maintenance');
        return true;
      }
      case 'completed':
        navigate(`/workshop/invoices?request=${r._id}`);
        return true;
      default:
        return false; // cancelled, declined, rejected
    }
  }, [navigate, start]);

  return {
    run,
    // Open the slot picker regardless of status — lets a confirmed job be
    // re-booked without pretending it is back in 'accepted'.
    schedule: (r) => { setActionError(null); setScheduling(r); },
    complete: (r) => complete.mutate({ id: r._id }),
    busyId,
    actionError,
    dismissError: () => setActionError(null),
    scheduleModal: {
      request: scheduling,
      onClose: () => setScheduling(null),
      onSubmit: (date, note) => schedule.mutate({ id: scheduling._id, date, note }),
      submitting: schedule.isPending,
      error: schedule.isError ? errText(schedule.error, 'Échec de la planification.') : null,
    },
  };
}
